import React, { useMemo } from 'react';
import { BarChart, PieChart } from '../ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportChartDataToCSV } from '../../utils/exportToCSV';

/**
 * Column Pie Widget Component
 * Modernized replacement for Column_Pie.js
 * Displays data as either column chart or pie chart with table view
 */
function ColumnPieWidget({ data, handlechangea }) {
    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Transform API data to chart format
    const chartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const dataset = apiData[0].columnDataSet1?.[0];
            if (!dataset || !dataset.labels || !dataset.data) return null;

            return {
                labels: dataset.labels,
                datasets: [{
                    label: data.YlabelString || 'Value',
                    data: dataset.data,
                    backgroundColor: [
                        '#f19f57', '#313131', '#3b82f6', '#10b981',
                        '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'
                    ],
                    borderWidth: 1
                }]
            };
        } catch (err) {
            console.error('Error transforming chart data:', err);
            return null;
        }
    }, [apiData, data]);

    // Transform to table format
    const tableData = useMemo(() => {
        if (!chartData) return [];

        return chartData.labels.map((label, index) => ({
            name: label,
            value: chartData.datasets[0].data[index]
        }));
    }, [chartData]);

    // Table columns
    const columns = [
        {
            key: 'name',
            label: data.DataType || 'Name',
            sortable: true
        },
        {
            key: 'value',
            label: data.YlabelString || 'Value',
            sortable: true,
            format: (val) => typeof val === 'number' ? val.toLocaleString() : val
        }
    ];

    // Export handler
    const handleExport = () => {
        if (chartData) {
            exportChartDataToCSV(chartData, data.title || 'chart-data');
        }
    };

    // Row click handler
    const handleRowClick = (row) => {
        if (handlechangea) {
            handlechangea({ target: { value: row.name } });
        }
    };

    // Chart component with dual chart types
    const chartComponent = chartData ? (
        <Tabs defaultValue="column" className="w-full">
            <TabsList className="bg-brand-light-1 mb-4">
                <TabsTrigger value="column" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white">
                    Column Chart
                </TabsTrigger>
                <TabsTrigger value="pie" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white">
                    Pie Chart
                </TabsTrigger>
            </TabsList>
            <TabsContent value="column" className="mt-0">
                <BarChart
                    data={chartData}
                    height={350}
                    options={{
                        onClick: (event, elements) => {
                            if (elements.length > 0 && handlechangea) {
                                const index = elements[0].index;
                                const label = chartData.labels[index];
                                handlechangea({ target: { value: label } });
                            }
                        }
                    }}
                />
            </TabsContent>
            <TabsContent value="pie" className="mt-0">
                <PieChart
                    data={chartData}
                    height={350}
                    options={{
                        onClick: (event, elements) => {
                            if (elements.length > 0 && handlechangea) {
                                const index = elements[0].index;
                                const label = chartData.labels[index];
                                handlechangea({ target: { value: label } });
                            }
                        }
                    }}
                />
            </TabsContent>
        </Tabs>
    ) : null;

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={chartComponent}
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        onRowClick={handlechangea ? handleRowClick : null}
                        pagination={{ enabled: true, itemsPerPage: 10 }}
                        sortable={true}
                        searchable={true}
                    />
                ) : null
            }
            onExport={handleExport}
            showToggle={true}
            showExport={true}
        />
    );
}

export default ColumnPieWidget;
